// Libs
import { DatePicker, type DatePickerProps } from "@mui/lab";
import type { MomentInput } from "moment";
import * as Yup from "yup";

// Utils
import { type TFunction, useTranslation } from "src/design-system/core/TranslationContext";
import { COMMON_TRANSLATION_NAMESPACE } from "src/i18n/config";
import type { IdName } from "src/types/IdName";
import { DayOfTheWeek } from "src/types/enum/DayOfTheWeek";
import { OfferStatus, ServiceTime } from "src/types/offerSlot";
import {
    FORMAT_DATE_WITH_TIME,
    formatDate,
    formatPrice,
    formatStringTime,
} from "src/utils/formatters";
import type { Domain, DomainBuilder } from "./Domain";
import { DomainKeyEnum } from "./DomainKeyEnum";

// Components
import type { NumberInputProps } from "src/components/generics/numberInput/NumberInput";
import { DomainAmountInput, DomainBooleanInput, type DomainBooleanInputProps } from "./components";

function buildEnumDomain<T>(
    key: DomainKeyEnum,
    _sourceEnum: T,
    translationKey: string
): DomainBuilder<T, unknown, unknown> {
    return {
        key,
        displayFormatterBuilder:
            (t: TFunction) =>
            (value: any): string =>
                value !== undefined ? t([translationKey, value].join(".")) : undefined,
    };
}

function resolveDomain<
    DT extends string | number | boolean | any = any,
    DCProps = any,
    ICProps = any,
>(
    t: TFunction,
    domainBuilder: DomainBuilder<DT, DCProps, ICProps> | Domain<DT, DCProps, ICProps>
): Domain<DT, DCProps, ICProps> {
    const displayFormatterBuilder = (domainBuilder as DomainBuilder).displayFormatterBuilder;
    const displayShortFormatterBuilder = (domainBuilder as DomainBuilder)
        .displayShortFormatterBuilder;

    if (displayFormatterBuilder || displayShortFormatterBuilder) {
        return {
            ...domainBuilder,
            displayFormatter: displayFormatterBuilder?.(t),
            displayShortFormatter: displayShortFormatterBuilder?.(t),
        };
    }

    return domainBuilder;
}

const DO_AMOUNT: Domain<number, unknown, NumberInputProps> = {
    key: DomainKeyEnum.Amount,
    displayFormatter: (value: number, options): string =>
        value && formatPrice(value, { ...options, noDecimal: true, showHyphenIfZero: true }),
    schema: Yup.number().integer().nullable(),
    defaultInputProps: { digits: 0 },
    InputComponent: DomainAmountInput,
};

const DO_AMOUNT_DECIMAL: Domain<number, unknown, NumberInputProps> = {
    key: DomainKeyEnum.AmountDecimal,
    displayFormatter: (value: number, options): string =>
        value && formatPrice(value, { ...options, noDecimal: false, showHyphenIfZero: true }),
    schema: Yup.number().integer().nullable(),
    defaultInputProps: { digits: 2 },
    InputComponent: DomainAmountInput,
};

const DO_BOOLEAN: DomainBuilder<boolean, unknown, DomainBooleanInputProps> = {
    key: DomainKeyEnum.Boolean,
    displayFormatterBuilder: (t: TFunction) => (value: boolean) =>
        value !== undefined
            ? t(
                  `${COMMON_TRANSLATION_NAMESPACE}:domains.${DomainKeyEnum.Boolean}.${value.toString()}`
              )
            : undefined,
    schema: Yup.boolean(),
    InputComponent: DomainBooleanInput,
};

const DO_DATE: Domain<MomentInput, unknown, DatePickerProps<any>> = {
    key: DomainKeyEnum.Date,
    displayFormatter: (value: MomentInput): string => value && formatDate(value),
    InputComponent: DatePicker,
    schema: Yup.date(),
};

const DO_DATE_TIME: Domain<MomentInput, unknown, DatePickerProps<any>> = {
    key: DomainKeyEnum.DateTime,
    displayFormatter: (value: MomentInput): string =>
        value && formatDate(value, FORMAT_DATE_WITH_TIME),
    InputComponent: DatePicker,
    schema: Yup.date(),
};

const DO_DAYS = buildEnumDomain(
    DomainKeyEnum.Days,
    DayOfTheWeek,
    `${COMMON_TRANSLATION_NAMESPACE}:enums.dows`
);

const DO_ID_NAME: Domain<IdName, unknown, unknown> = {
    key: DomainKeyEnum.IdName,
    displayFormatter: (value: IdName) => value?.name,
};

const DO_MARKETING_ACTION_STATUS = buildEnumDomain(
    DomainKeyEnum.MarketingActionStatus,
    OfferStatus,
    `${COMMON_TRANSLATION_NAMESPACE}:enums.offerslot-status`
);

const DO_SERVICE_TIME = buildEnumDomain(
    DomainKeyEnum.ServiceTime,
    ServiceTime,
    `${COMMON_TRANSLATION_NAMESPACE}:enums.serviceTime`
);

const DO_TIME: DomainBuilder<string, unknown, unknown> = {
    key: DomainKeyEnum.Time,
    displayFormatterBuilder: (t: TFunction) => (value: string) =>
        value &&
        formatStringTime(value, (string) => t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`)),
    schema: Yup.string().matches(/[0-9]{1,2}:[0-9]{1,2}(:[0-9]{1,2})?/),
};

const domainsConfig = {
    [DomainKeyEnum.Amount]: DO_AMOUNT,
    [DomainKeyEnum.AmountDecimal]: DO_AMOUNT_DECIMAL,
    [DomainKeyEnum.Boolean]: DO_BOOLEAN,
    [DomainKeyEnum.Date]: DO_DATE,
    [DomainKeyEnum.DateTime]: DO_DATE_TIME,
    [DomainKeyEnum.Days]: DO_DAYS,
    [DomainKeyEnum.IdName]: DO_ID_NAME,
    [DomainKeyEnum.MarketingActionStatus]: DO_MARKETING_ACTION_STATUS,
    [DomainKeyEnum.ServiceTime]: DO_SERVICE_TIME,
    [DomainKeyEnum.Time]: DO_TIME,
} as const;

export type DomainResolved<T> = {
    readonly [P in keyof T]: T[P] extends DomainBuilder<infer DT, infer DCProps, infer ICProps>
        ? Domain<DT, DCProps, ICProps>
        : T[P];
};

export type DomainBuilderType = typeof domainsConfig;
export type DomainType = DomainResolved<DomainBuilderType>;

export function useBuildDomains(): DomainType {
    const { t } = useTranslation();

    const domains = Object.entries(domainsConfig).reduce(
        (acc, [key, domain]) => ({
            ...acc,
            [key]: resolveDomain(t, domain as unknown as Domain),
        }),
        {}
    );

    return domains as DomainType;
}
